/**
 * window.alert / window.confirm の代わりに使うダイアログ。
 * 外部スクリプトや HTML 文字列を使わず、本文は textContent で入れる。
 * Esc と背景クリックは「閉じる」（確認では「キャンセル」）として扱う。
 */
type DialogOptions = { okLabel?: string; cancelLabel?: string };

const labels = { ok: 'OK', cancel: 'Cancel' };

/** ボタンの既定ラベル。表示言語が決まった時点で一度だけ設定する。 */
export function setDialogLabels({ ok, cancel }: { ok?: string; cancel?: string }): void {
  if (ok) labels.ok = ok;
  if (cancel) labels.cancel = cancel;
}

function openDialog(
  message: string,
  buttons: { label: string; value: boolean; primary?: boolean }[]
): Promise<boolean> {
  return new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'message-dialog';

    const body = document.createElement('p');
    body.className = 'message-dialog-body';
    body.textContent = message;

    const actions = document.createElement('div');
    actions.className = 'message-dialog-actions';
    let primaryButton: HTMLButtonElement | null = null;
    for (const item of buttons) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = item.primary ? 'message-dialog-ok-button' : 'message-dialog-cancel-button';
      button.textContent = item.label;
      button.addEventListener('click', () => dialog.close(item.value ? 'true' : ''));
      actions.append(button);
      if (item.primary) primaryButton = button;
    }
    dialog.append(body, actions);

    // 背景（ダイアログ外）のクリックで閉じる
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close('');
    });
    dialog.addEventListener('close', () => {
      const accepted = dialog.returnValue === 'true';
      dialog.remove();
      resolve(accepted);
    });
    document.body.append(dialog);
    dialog.showModal();
    primaryButton?.focus();
  });
}

/** お知らせを表示する。閉じたときに解決する。 */
export async function showMessage(message: string, { okLabel = labels.ok }: DialogOptions = {}): Promise<void> {
  await openDialog(message, [{ label: okLabel, value: true, primary: true }]);
}

/** OK / キャンセルの確認を表示する。OK なら true、それ以外は false で解決する。 */
export function showConfirm(
  message: string,
  { okLabel = labels.ok, cancelLabel = labels.cancel }: DialogOptions = {}
): Promise<boolean> {
  return openDialog(message, [
    { label: cancelLabel, value: false },
    { label: okLabel, value: true, primary: true },
  ]);
}
